import { Component, Inject, Injector, OnDestroy, Output, EventEmitter, ViewChild, TemplateRef } from '@angular/core';

import { Subscription, Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { FilterService } from '../../../../../../Common/Filter/services/abstract-filter.service';
import { ButtonTypesEnum } from '../../../../../../Common/Forms/enums/button-types.enum';
import { ChipModel } from '../../../../../../Common/Forms/models/chip.model';
import { TwitterAvatarSize } from '../../../../../../Common/Misc/pipes/TwitterAvatarSize/twitter-avatar-size.pipe';
import { CoreDialogService } from '../../../../../../Core/Dialogs/services/dialog.service';
import { RecipientResultModel } from '../../../../models/recipient.result.model';
import { TargetingListService } from '../../../../services/targeting-list.service';
import { TargetingSearchService } from '../../../../services/targeting-search.service';
import { ListPickerService } from '../../../../../../Common/RhpContexts/services/list-picker.service';
import { ListPickerDialogComponent } from '../../../../../../Common/RhpContexts/components/ListPickerDialog/list-picker-dialog.component';
import { RecipientSearchService } from './recipient-search.service';
import { TabComponent } from '../../../../../../Common/Layout/components/Tab/tab.component';
import { RightSidebarComponent } from '../../../../../../Common/Layout/components/RightSidebar/right-sidebar.component';
import { FilterGroupModel } from '../../../../../../Common/Filter/models/filter-group.model';
import { AdditionalRecipientSearchService } from './additional-recipient-search.service';
import { DistributionService } from '../../../../../../Common/TargetingShared/services/distribution.service';
import { DistributionStatus } from '../../../../../../Common/TargetingShared/enum/distribution-status.enum';
import { DistributionReportStat } from '../../../../../../Common/TargetingShared/model/statisticsReport/distribution-report-stat-api.model';
import { TargetingListList } from '../../../../models/targeting-list-list.model';
import { AutocompleteCustomOption, AppearCondition } from '../../../../../../Common/Forms/components/Autocomplete/autocomplete-custom-option.model';
import { messageSeverityEnum } from '../../../../../../Common/Misc/enums/message-severity.enum';
import { ChipContainerComponent } from '../../../../../../Common/Forms/components/ChipContainer/chip-container.component';
import { AutocompleteComponent } from '../../../../../../Common/Forms/components/Autocomplete/autocomplete.component';
import {
    TargetingBatchActionsDialogComponent,
    TargetingBatchActionsEnum
} from '../../../../../../Common/TargetingShared/components/TargetingBatchActionsDialog/targeting-batch-actions-dialog.component';
import { SourceType } from '../../../../services/source-picker-service';
import { DistributionModel } from '../../../../models/distribution.model';
import {
    AddAllRecipientsToListsApiModel,
    AddAllRecipientsToListsContext
} from '../../../../../../api/distributions/model/add-all-recipients-to-lists.api.model';
import { RecipientRhpTakeoverEvent } from './RecipientCard/recipient-card.component';
import { DistributionSummaryModel } from '@Common/TargetingShared/model/distribution-summary-model';
import { TargetingAddToTypeEnum } from '@Discover/Targeting/enums/targeting-add-to-type.enum';
import { targetingAddToOptions } from '@Discover/Targeting/models/targeting-add-to-options.model';
import { DropdownOptionModel } from '@Common/Forms/models/dropdown-option.model';
import { DistributionPickerService } from '@Common/RhpContexts/services/distribution-picker.service';
import { DistPickerDialogComponent } from '@Common/RhpContexts/components/DistPickerDialog/dist-picker-dialog.component';
import { EmailConversationService } from '../../../../services/email-conversation.service';
import { RepliesSearchApi } from '../../../../../../api/targeting/model/replies-search-api';
import { CommonApiErrorEnum } from '../../../../../../api/genericApi/enums/common-api-error.enum';
import { TargetingList } from '../../../../models/targeting-list.model';
import { ResourceType, WebsocketAwareNeptuneService } from '../../../../../../Bootstrap/services/websocket-aware-neptune.service';


export enum DistributionRecipientType {
    contacts,
    others,
}

@Component({
    selector: 'agpr-recipient-search-tab',
    templateUrl: './recipient-search-tab.component.html',
    styleUrls: [
        './recipient-search-tab.component.scss',
        '../../../TargetingResults/targeting-results.component.scss', // List picker styling
        '../../../../../../Common/RhpContexts/components/EntityListTab/entity-list-tab.component.scss'
    ],
    providers: [
        {
            provide: TabComponent,
            useExisting: RecipientSearchTabComponent
        },
        {
            provide: 'PaginatorServiceInterface',
            useExisting: RecipientSearchService
        },
        {
            provide: FilterService,
            useExisting: RecipientSearchService
        },
        {
            provide: 'SortOrderServiceInterface',
            useExisting: RecipientSearchService
        },
        {
            provide: 'RecipientSearchListPickerService',
            useClass: ListPickerService
        },
        {
            provide: ListPickerService,
            useExisting: 'RecipientSearchListPickerService'
        },
        {
            provide: 'RecipientSearchDistributionPickerService',
            useClass: DistributionPickerService
        },
        {
            provide: DistributionPickerService,
            useExisting: 'RecipientSearchDistributionPickerService'
        }
    ],
    standalone: false
})
export class RecipientSearchTabComponent extends TabComponent implements OnDestroy {

    @Output() showRhpTakeOver: EventEmitter<RecipientRhpTakeoverEvent> = new EventEmitter<RecipientRhpTakeoverEvent>();

    @ViewChild('tabHeader')
    protected declare _header: TemplateRef<any>;
    public get header(): TemplateRef<any> {
        return this._header;
    }
    public set header(value: TemplateRef<any>) {
        this._header = value;
    }
    @ViewChild('tabFooter') declare footer: TemplateRef<any>;
    @ViewChild('listChipsComponent') public listChipsComponent: ChipContainerComponent;
    @ViewChild('listBuilderDropdown') public listBuilderDropdown: AutocompleteComponent;

    private subscriptions: Subscription[] = [];

    id: string = 'recipientSearch';

    results: RecipientResultModel[];
    distribution: DistributionModel;
    selectedResults: RecipientResultModel[] = [];
    totalResults: number;
    allSelected: boolean = false;

    additionalRecipients: RecipientResultModel[];
    totalAdditionalRecipients: number;

    targetingLists: TargetingListList[];
    selectedListsChips: ChipModel[] = [];
    selectedLists: TargetingListList[] = [];
    targetingLists$: Observable<TargetingListList[]>;
    freshCreatedId: number;
    workingListsLocked: boolean = false;

    loading = false;
    additionalRecipientLoading = false;
    chipsVisible = false;

    distributionFailed: boolean = false;

    selectedTabIndex: DistributionRecipientType = DistributionRecipientType.contacts;
    selectedStatType: DistributionReportStat;
    selectedLink: string;

    readonly twitterAvatarSizes = TwitterAvatarSize;
    readonly buttonTypes = ButtonTypesEnum;
    readonly distributionRecipientType = DistributionRecipientType;

    readonly listBuilderOptions: AutocompleteCustomOption[] = [{
        label: 'Discover.listBuilder.createList',
        action: (listName: string) => {
            if (listName.length === 0) {

                return;
            }

            this.targetingListService.isNameDuplicated$(listName).pipe(
                switchMap((duplicate) => {
                    if (duplicate) {
                        throw new Error(CommonApiErrorEnum.duplicateName);
                    } else {
                        const list = new TargetingList({name: listName});
                        return this.targetingListService.create(list);
                    }
                })
            ).subscribe({
                next: (id) => {
                    this.freshCreatedId = id;
                    this.newlyAddedList();
                },
                error: () => {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Discover.dialogs.newList.duplicate',
                    });
                }
            });
        },
        appearCondition: AppearCondition.noExactMatchInSuggestions,
        clearOnSelect: false
    }];

    readonly targetingAddToOptions = targetingAddToOptions;
    readonly targetingAddToTypes = TargetingAddToTypeEnum;
    addToType: TargetingAddToTypeEnum = TargetingAddToTypeEnum.LIST;
    distributions: DropdownOptionModel<DistributionSummaryModel>[];
    distributions$: Observable<DistributionSummaryModel[]>;
    selectedDistribution: DropdownOptionModel<DistributionSummaryModel>;

    selectedDistributionsChips: ChipModel[] = [];
    selectedDistributions: DistributionSummaryModel[] = [];
    selectedDistributionsTooltip: string;
    workingDistributionsLocked: boolean = false;

    constructor(
        private rhp: RightSidebarComponent,
        private distributionService: DistributionService,
        private messageService: MessageService,
        public recipientSearchService: RecipientSearchService,
        public targetingSearchService: TargetingSearchService,
        public coreDialogService: CoreDialogService,
        public targetingListService: TargetingListService,
        public additionalRecipientSearchService: AdditionalRecipientSearchService,
        public injector: Injector,
        private emailConversationService: EmailConversationService,
        private websocketService: WebsocketAwareNeptuneService,
        @Inject('RecipientSearchListPickerService') public listPickerService: ListPickerService,
        @Inject('RecipientSearchDistributionPickerService') public distributionPickerService: DistributionPickerService,
    ) {
        super();
        this.targetingLists$ = this.targetingListService.all$;
        listPickerService.useLocking = true;

        distributionPickerService.useLocking = true;
        distributionPickerService.loadWorkingDistributionsFromSession();
        this.distributions$ = this.distributionService.getDistributionSummaries().pipe(
            filter(x => !!x),
            map(dist => dist.filter(element => element.status === DistributionStatus.draft))
        );

        this.subscriptions.push(
            this.distributionService.getFocusedResource().pipe(filter(x => !!x)).subscribe((dist) => {
                this.distribution = dist;
                this.distributionFailed = dist.status === DistributionStatus.failed;
                if (this.distributionFailed && this.results && this.additionalRecipients) {
                    this.results.forEach(x => x.topStat = DistributionReportStat.NOT_SENT);
                    this.additionalRecipients.forEach(x => x.topStat = DistributionReportStat.NOT_SENT);
                }
            }),
            this.recipientSearchService.getResults().subscribe(results => {
                this.results = results;
                if (this.distributionFailed) {
                    this.results?.forEach(x => x.topStat = DistributionReportStat.NOT_SENT);
                }

                this.selectNone();
            }),
            this.recipientSearchService.getItemCount().subscribe(total => this.totalResults = total),
            this.recipientSearchService.getFilters().subscribe(filters => {
                // eslint-disable-next-line @typescript-eslint/no-shadow
                this.chipsVisible = !!filters.find(filter => {
                    if (filter instanceof FilterGroupModel) {
                        return filter.children.find(x => x.chips && x.chips.length > 0);
                    } else {
                        return filter.chips && filter.chips.length > 0;
                    }
                });
            }),
            this.recipientSearchService.getPageCriteria().subscribe(pageCriteria => this.selectNone()),
            this.recipientSearchService.getSearchLoading().subscribe(loading => this.loading = loading),
            this.additionalRecipientSearchService.getSearchLoading().subscribe(loading => this.additionalRecipientLoading = loading),
            this.additionalRecipientSearchService.getItemCount().subscribe(total => this.totalAdditionalRecipients = total),
            this.additionalRecipientSearchService.getDisplayResults().subscribe(results => {
                this.additionalRecipients = results;
                if (this.distributionFailed) {
                    this.additionalRecipients?.forEach(x => x.topStat = DistributionReportStat.NOT_SENT);
                }
            }),
            this.listPickerService.getWorkingLists().pipe(
                filter(x => !!x))
                .subscribe(lists => {
                    this.selectedLists = lists;
                    this.selectedListsChips = lists.map(list => ({
                        label: list.name
                    }));
                }),
            this.listPickerService.getWorkingListsLock().subscribe(lock => {
                this.workingListsLocked = lock;
            }),
            this.distributionPickerService.getWorkingDistributions().pipe(
                filter(x => !!x))
                .subscribe(distributions => {
                    this.selectedDistributions = distributions;
                    this.selectedDistributionsChips = distributions.map(list => ({
                        label: list.name
                    }));
                }),
            this.distributionPickerService.getWorkingDistributionsLock().subscribe(lock => {
                this.workingDistributionsLocked = lock;
                if (lock) {
                    this.addToType = TargetingAddToTypeEnum.DISTRIBUTION;
                }
            }),
            this.targetingListService.all$.subscribe(
                (lists) => {
                    this.targetingLists = lists;
                    // remove lists that have been deleted
                    this.selectedLists = this.selectedLists.filter((selectedList) => {
                        return !!lists.find(list => selectedList.id === list.id);
                    });

                    this.newlyAddedList();
                }
            ),
            this.recipientSearchService.getSelectedStatType().pipe(
                filter(x => !!x)
            ).subscribe(statType => {
                this.selectedStatType = statType;
                this.selectedLink = undefined;
            }),
            this.recipientSearchService.getSelectedLink().pipe(
                filter(x => !!x)
            ).subscribe(link => {
                this.selectedLink = link;
                this.selectedStatType = undefined;
            }),
            this.websocketService.getWebsocketMessages().pipe(
                filter(x => !!x)
            ).subscribe(message => {
                // Catch the general websocket distribution message
                if (Object.keys(message).length === 1 && message.resourceType === ResourceType.distributions) {
                    this.recipientSearchService.doSearch();
                }
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    newlyAddedList() {
        if (this.freshCreatedId) {
            const freshCreatedList = this.targetingLists.find(x => x.id === this.freshCreatedId);
            this.listPickerService.addWorkingList(freshCreatedList);
            this.listPickerService.setWorkingListsLock(true);
            this.freshCreatedId = undefined;
        }
    }

    updateListBuilder() {
        this.listPickerService.setWorkingLists(this.selectedLists);
        this.selectedListsChips = this.selectedLists.map(workingList => ({
            label: workingList.name
        }));

        this.listPickerService.setWorkingListsLock(true);
    }

    openListPicker(addMode: boolean = true) {
        this.coreDialogService.open(ListPickerDialogComponent, {
            inputs: {
                addMode: addMode
            },
            outputs: {}
        },
        this.injector);
    }

    openDistPicker(addMode: boolean = true) {
        this.coreDialogService.open(DistPickerDialogComponent, {
            inputs: {
                addMode: addMode
            },
            outputs: {}
        },
        this.injector);
    }

    addToList() {
        this.listPickerService.setSelectedEntities(this.selectedResults);
        const apiBody = {
            query: this.recipientSearchService.getCurrentSearchContext(),
            statsType: this.selectedStatType,
            link: this.selectedLink,
            listIds: null
        } as AddAllRecipientsToListsApiModel;
        const addAllRecipientsToListsContext = {
            distributionId: this.distribution.id,
            apiBody: apiBody
        } as AddAllRecipientsToListsContext;
        this.listPickerService.setAddRecipientsToListsContext(addAllRecipientsToListsContext);
        if (this.selectedListsChips?.length > 0) {
            this.listPickerService.addSelectedEntitiesToWorkingLists();
        } else {
            this.openListPicker();
        }
    }

    addTo() {
        if (this.addToType === this.targetingAddToTypes.LIST) {
            this.addToList();
        } else {
            this.addToDistribution();
        }
    }

    addToDistribution() {
        this.distributionPickerService.setSelectedEntities(this.selectedResults);
        if (this.selectedListsChips?.length > 0) {
            this.distributionPickerService.addSelectedEntitiesToWorkingDists();
        } else {
            this.openDistPicker();
        }
    }

    clearListChips() {
        this.listChipsComponent.chipPopover.hide();
        this.listPickerService.clearWorkingLists();
    }
    clearDistributionChips() {
        this.listChipsComponent.chipPopover.hide();
        this.distributionPickerService.clearWorkingDistributions();
    }

    toggleSelectAll(): void {
        if (this.results.length <= this.selectedResults.length) {
            this.selectNone();
        } else {
            this.selectAllInPage();
        }
    }

    selectAllInPage(): void {
        this.selectedResults = this.results.slice();
    }

    selectNone(): void {
        this.listPickerService.setAllResultsSelected(false);
        this.distributionPickerService.setAllResultsSelected(false);
        this.allSelected = false;
        this.selectedResults = [];
    }

    selectAllResults() {
        this.listPickerService.setAllResultsSelected(true, this.totalResults);
        this.distributionPickerService.setAllResultsSelected(true, this.totalResults);
        this.allSelected = true;
    }

    selectTargetingType() {
        this.listPickerService.clearWorkingLists();
        this.distributionPickerService.clearWorkingDistributions();
    }

    updateDistributionBuilder() {
        this.distributionPickerService.setWorkingDistributions(this.selectedDistributions);
        this.selectedDistributionsChips = this.selectedDistributions.map(workingDistribution => ({
            label: workingDistribution.name
        }));
        this.distributionPickerService.setWorkingDistributionsLock(true);
    }

    tabChange(index: DistributionRecipientType): void {
        if (this.selectedTabIndex === index) {
            return;
        }

        this.selectedTabIndex = index;
    }

    openBatchActions() {
        this.coreDialogService.open(TargetingBatchActionsDialogComponent, {
            inputs: {
                selectedEntities: this.selectedResults,
                allResultsSelected: this.allSelected,
                resultsCount: this.allSelected ? this.totalResults : this.selectedResults.length,
                allowDistributionType: true,
                initialSourceType: SourceType.distributions,
                initialSourceId: this.distribution.id,
                distributionReportStatType: this.selectedStatType,
                distributionSelectedLink: this.selectedLink,
                id: 'distributionRecipientsBatchActionsDialog'
            },
            outputs: {}
        });
    }

    composeEmail(id: string): void {
        this.emailConversationService.composeEmail(id);
    }

    viewConversation(id: string): void {
        this.emailConversationService.viewConversation({
            distributionId: this.distribution.id,
            grantId: this.distribution?.sendFrom?.grantId
        } as RepliesSearchApi, id);
    }
}
